'use client'

import { Button } from '@/components/ui/button'
import { Heading, Body } from '@/components/ui/typography'
import { useEffect } from 'react'
import { clientEnv } from '@/lib/env'
import { decisionToGranted } from '@/lib/consent'
import { readConsentCookieClient } from '@/lib/consent.client'
import { errorTypeOf, fingerprintFor } from '@/lib/errors-shared'
import { postHogBeforeSend } from '@/lib/posthog-before-send'
import { SUPPORT_EMAIL, SUPPORT_EMAIL_HREF } from '@/lib/support'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    void (async () => {
      try {
        // global-error renders outside layout providers — PostHogProvider never mounts, so init here.
        if (decisionToGranted(readConsentCookieClient()) !== true) return
        if (!clientEnv.NEXT_PUBLIC_POSTHOG_KEY || !clientEnv.NEXT_PUBLIC_POSTHOG_HOST) return
        const { default: posthog } = await import('posthog-js')
        if (!posthog.__loaded) {
          // Mirror PostHogProvider's init — once posthog-js initialises, re-init is a no-op,
          // so a minimal config here would silently drop the sanitiser for the rest of the session.
          posthog.init(clientEnv.NEXT_PUBLIC_POSTHOG_KEY as string, {
            api_host: clientEnv.NEXT_PUBLIC_POSTHOG_HOST as string,
            person_profiles: 'identified_only',
            capture_pageview: false,
            disable_session_recording: true,
            defaults: '2026-01-30',
            before_send: postHogBeforeSend,
          })
        }
        const properties: Record<string, unknown> = {
          $exception_source: 'app.global-error',
          digest: error.digest,
          errorType: errorTypeOf(error),
        }
        const fingerprint = fingerprintFor(error)
        if (fingerprint) {
          properties.$exception_fingerprint = fingerprint
        }
        posthog.captureException(error, properties)
      } catch {
        // Swallow — capture must never crash the error UI.
      }
    })()
  }, [error])

  return (
    <html lang="en">
      <body>
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
          <div className="max-w-md text-center">
            <div className="flex flex-col gap-3">
              <Heading>Something went wrong!</Heading>
              <Body>An unexpected error occurred. We apologize for the inconvenience.</Body>
              {error.digest && <Body variant="muted">Error ID: {error.digest}</Body>}
            </div>
            <div className="mt-4">
              <Body variant="muted">
                Need help? Email{' '}
                <a className="underline" href={SUPPORT_EMAIL_HREF}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </Body>
            </div>
            <Button onClick={reset}>Try again</Button>
          </div>
        </div>
      </body>
    </html>
  )
}
